import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import {
  Button,
  Dropdown,
  DropdownProps,
  Form,
  FormField,
  FormGroup,
  Icon,
} from "semantic-ui-react";

import moment, { Moment } from "moment";

import LeaveRequestSerializer from "issara-sdk/types/LeaveRequestSerializer_apps_HRM";
// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";

import CardLeaveRequestPanel from "./CardLeaveRequestPanel";
import TableTimeShift from "./TableTimeShift";

// Interface
import {
  CARD_NAME,
  LeaveRemainingType,
  PickedProps,
  RunSequence as SelfServiceRunSequence,
  SetProp as SelfServiceSetProp,
} from "./sequence/SelfServiceSchedule";
import {
  ACTIONS,
  BTN_ACTS,
  getDaysOfMonth,
  RunSequence as SetupWorkRunSequence,
  SetProp as SetupWorkSetProp,
  State as SetupWorkState,
} from "./sequence/SetupWorkSchedule";

type CardSelfServiceWorkScheduleProps = PickedProps & {
  setProp: SelfServiceSetProp & SetupWorkSetProp;
  // seq
  runSequence: SelfServiceRunSequence & SetupWorkRunSequence;
  SetupWorkScheduleSequence: SetupWorkState["SetupWorkScheduleSequence"];
  // data
  leaveRemaining: LeaveRemainingType;
  leaveTypeList: LeaveTypeSerializer[];
  paramKey: string;
  userProfile: Record<string, any> | null;
};

const CardSelfServiceWorkSchedule = (props: CardSelfServiceWorkScheduleProps) => {
  const [openRequestPanel, setOpenRequestPanel] = useState<boolean>(false);
  const [viewMonth, setViewMonth] = useState<Moment>(() => moment());

  useEffect(() => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      byEmployee: true,
      divisionId: props.selectedDivision?.id,
      employee: props.userProfile?.employee,
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  useEffect(
    () => () => {
      props.runSequence({ sequence: "SetupWorkSchedule", clear: true });
    },
    []
  );

  // Clear Data + Panel After Save Success
  useEffect(() => {
    if (!props.successMessage?.[CARD_NAME]) {
      return;
    }

    handleRefresh(viewMonth, true);
    setOpenRequestPanel(false);

    props.setProp("SelfServiceScheduleSequence.leaveRemaining", {});
  }, [props.successMessage]);

  const listDaysOfMonth: Moment[] = useMemo(() => getDaysOfMonth(viewMonth), [viewMonth]);

  const timeShifts = useMemo(() => {
    const yearMonth = viewMonth.format("YYYY-MM");

    return props.SetupWorkScheduleSequence?.workShifts?.[yearMonth] ?? [];
  }, [props.SetupWorkScheduleSequence?.workShifts, viewMonth]);

  const filteredTimeShifts = useMemo(() => {
    const filter = props.SetupWorkScheduleSequence?.filter ?? {};

    return timeShifts.filter((item) => !filter.employee || item.employee === filter.employee);
  }, [
    props.SetupWorkScheduleSequence?.filter,
    props.SetupWorkScheduleSequence?.workShifts,
    timeShifts,
    viewMonth,
  ]);

  const holidays = useMemo(() => {
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];
    const yearMonth = viewMonth.format("YYYY-MM");
    const [year, month] = yearMonth.split("-");

    const index = workShiftSettings.findIndex(
      (item) => item.ad_year === Number(year) && item.month === Number(month)
    );

    if (index === -1) {
      return { index, items: [] };
    }

    return { index, items: workShiftSettings[index].holiday };
  }, [props.SetupWorkScheduleSequence?.workShiftSettings, viewMonth]);

  const filteredHolidays = useMemo(() => holidays.items.filter((item) => item.active), [holidays]);

  const yearMonthOptions = useMemo(() => {
    const yearMonth = moment().format("YYYY-MM");
    const workShiftSettings = props.SetupWorkScheduleSequence?.workShiftSettings ?? [];

    const formatted = workShiftSettings.map((item) =>
      moment(`${item.ad_year}-${item.month}`).format("YYYY-MM")
    );

    const allYearMonth = [...new Set([yearMonth, ...formatted])].sort((a, b) => a.localeCompare(b));

    return allYearMonth.map((text) => ({
      key: text,
      text: moment(text).format("MMMM YYYY"),
      value: text,
    }));
  }, [props.SetupWorkScheduleSequence?.workShiftSettings]);

  const payrollRecordEmployeeIds = useMemo(() => {
    const payrollRecords = props.SetupWorkScheduleSequence?.payrollRecordEmployeeIds ?? {};
    const yearMonth = viewMonth.format("YYYY-MM");

    return payrollRecords[yearMonth] ?? [];
  }, [props.SetupWorkScheduleSequence?.payrollRecordEmployeeIds, viewMonth]);

  const handleSubmit = (form: Partial<LeaveRequestSerializer>, files: { file: File }[]) => {
    props.runSequence({
      sequence: "SelfServiceSchedule",
      action: "REQUEST",
      files,
      menu: "WorkSchedule",
      saveLeaveRequest: form,
    });
  };

  const handleCancel = () => {
    props.setProp("SelfServiceScheduleSequence.leaveRemaining", {});

    setOpenRequestPanel(false);
  };

  const handleRefresh = (value: Moment, isRefresh = false) => {
    props.runSequence({
      sequence: "SetupWorkSchedule",
      action: ACTIONS.SEARCH,
      refreshMode: isRefresh ? "all" : undefined,
      viewMonth: value,
    });
  };

  const handleChangeMonth = (e: SyntheticEvent, data: DropdownProps) => {
    const value = moment(String(data.value));

    setViewMonth(value);

    handleRefresh(value);
  };

  return (
    <div
      style={{
        height: "calc(-7.5rem + 100vh)",
        margin: "-10px",
        overflowX: "hidden",
        overflowY: "auto",
        padding: "10px",
      }}
    >
      {/* Filter Section */}
      <Form style={{ margin: "-0.25rem 0 -0.25rem 0" }} size="tiny">
        <FormGroup>
          <FormField>
            <label>Select month</label>
            <Dropdown
              id="CardSelfServiceWorkSchedule-Dropdown-MonthOptions"
              value={viewMonth.format("YYYY-MM")}
              search
              selection
              options={yearMonthOptions}
              onChange={handleChangeMonth}
              loading={
                !!props.buttonLoadCheck?.[`${BTN_ACTS.SEARCH}_CHANGE`] ||
                !!props.buttonLoadCheck?.[BTN_ACTS.SEARCH]
              }
            />
          </FormField>
          <FormField style={{ flex: 1 }} />
          <FormField>
            <label>{"\u00A0"}</label>
            <Button
              id="CardSelfServiceWorkSchedule-Button-OpenPanel"
              color="green"
              size="tiny"
              onClick={() => {
                setOpenRequestPanel(true);
              }}
            >
              <div style={{ display: "flex", gap: "1rem" }}>
                <Icon name="add user" />
                LEAVE REQUEST
              </div>
            </Button>
          </FormField>
        </FormGroup>
      </Form>
      <div id="card-setup-work-schedule" style={{ height: "auto", padding: 0 }}>
        <div id="calender-working-shift">
          <TableTimeShift
            data={filteredTimeShifts}
            holidays={filteredHolidays}
            listMonths={listDaysOfMonth}
            payrollRecordEmployeeIds={payrollRecordEmployeeIds}
            showSummaryColumn
          />
        </div>
      </div>

      {/* Leave Request Section */}
      {openRequestPanel && (
        <div>
          <Form style={{ marginBottom: "0px", marginLeft: "1rem", marginTop: "1rem" }}>
            <FormGroup>
              <FormField>
                <label style={{ fontSize: "18px", fontWeight: "bold" }}>Leave Request</label>
              </FormField>
            </FormGroup>
          </Form>

          <CardLeaveRequestPanel
            setProp={props.setProp}
            runSequence={props.runSequence}
            leaveRemaining={props.leaveRemaining}
            leaveTypeList={props.leaveTypeList}
            mode="create"
            paramKey={props.paramKey}
            buttonLoadCheck={props.buttonLoadCheck}
            successMessage={props.successMessage}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default CardSelfServiceWorkSchedule;
